import React from 'react';
import { MapPin, Clock, Navigation, Globe, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { useTheme } from '../../context/ThemeContext';

const LocationInfo = React.memo(({ location }) => {
  const { isDark } = useTheme();
  // Real PCC service schedule - format: "Day Time" with type as subtitle
  const serviceSchedule = [
    { label: 'Dimanche 10h00 - 12h30', type: 'Culte principal' },
    { label: 'Dimanche 16h00', type: "Culte de l'après-midi" },
    { label: 'Vendredi 19h30', type: 'Réunion de prière' },
  ];

  // Real PCC coordinates for the map (7 Rue Pascal, 93120 La Courneuve)
  const mapEmbedUrl = "https://maps.google.com/maps?q=7+Rue+Pascal,+93120+La+Courneuve&z=15&output=embed";

  return (
    <div className={`py-10 px-6 transition-colors duration-200 ${isDark ? 'bg-[#272727]' : 'bg-white'}`}>
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Map */}
          <Card className={`overflow-hidden rounded-2xl shadow-md ${isDark ? 'bg-[#333333] border-[#444444]' : ''}`}>
            <CardContent className="p-0">
              <div className={`relative h-64 md:h-80 ${isDark ? 'bg-[#3A3A3A]' : 'bg-gray-200'}`}>
                <iframe
                  src={mapEmbedUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="PCC Location"
                />
              </div>
              
              <div className={`p-5 ${isDark ? 'bg-[#333333]' : 'bg-white'}`}>
                <div className="flex items-start space-x-3 mb-4">
                  <MapPin size={20} className="text-pcc-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className={`font-bold ${isDark ? 'text-[#F2F1EB]' : 'text-gray-900'}`}>{location.name}</h3>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{location.address}</p>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{location.city}</p>
                  </div>
                </div>
                
                <Button
                  onClick={() => window.open(location.mapsUrl, '_blank')}
                  className="w-full bg-pcc-primary hover:bg-pcc-primary-dark text-white font-semibold rounded-xl"
                >
                  <Navigation size={16} className="mr-2" />
                  Itinéraire
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Schedule & Info */}
          <div className="space-y-6">
            {/* Schedule */}
            <Card className={`rounded-2xl shadow-md ${isDark ? 'bg-[#333333] border-[#444444]' : ''}`}>
              <CardContent className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-pcc-primary/10 rounded-xl flex items-center justify-center">
                    <Clock size={20} className="text-pcc-primary" />
                  </div>
                  <h3 className={`text-xl font-bold ${isDark ? 'text-[#F2F1EB]' : 'text-gray-900'}`}>Nos rendez-vous</h3>
                </div>
                
                <div className="space-y-4">
                  {serviceSchedule.map((service, index) => (
                    <div 
                      key={index}
                      className={`flex items-center justify-between py-3 border-b last:border-0 ${isDark ? 'border-[#444444]' : 'border-gray-100'}`}
                    >
                      <div>
                        <p className={`font-semibold ${isDark ? 'text-[#F2F1EB]' : 'text-gray-900'}`}>{service.type}</p>
                      </div>
                      <span className="text-pcc-primary font-medium">{service.label}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* First Visit — UX 4: "En savoir plus" replaced with a
                more specific, action-clear "Site internet" button. The
                globe icon + external-link arrow make the destination
                obvious at a glance; bigger padding (py-3) and the
                explicit pariscentrechretien.com subtitle line give
                visitors confidence they're going to the church's
                main site, not a generic landing. */}
            <Card className="rounded-2xl shadow-md bg-gradient-to-br from-pcc-primary to-pcc-primary-dark text-white">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-3">Première visite ?</h3>
                <p className="text-white/90 mb-4 leading-relaxed">
                  Venez nous rejoindre le dimanche à partir de 10h00 au 7 Rue Pascal, 93120 La Courneuve. Nous serions ravis de vous accueillir !
                </p>
                <Button
                  className={`font-semibold rounded-xl px-5 py-3 inline-flex items-center gap-2 ${isDark ? 'bg-white text-pcc-primary hover:bg-gray-100' : 'bg-white text-pcc-primary hover:bg-gray-100'}`}
                  onClick={() => window.open('https://pariscentrechretien.com', '_blank', 'noopener,noreferrer')}
                  data-testid="home-site-internet-btn"
                >
                  <Globe size={18} />
                  <span>Site internet</span>
                  <ExternalLink size={14} className="opacity-70" />
                </Button>
                <p className="text-white/70 text-xs mt-2">
                  pariscentrechretien.com
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
});

export default LocationInfo;
